// Centralized auth context — single source of truth for the logged-in user's
// role, staff/superuser flags, and owned stores. Bootstraps from the existing
// localStorage('accessToken') so it composes with the legacy login flow
// without forcing a rewrite of LoginPage/SignupPage.
//
// The role is used by RequireRole to gate /admin/* and /merchant/* routes and
// by LoginPage post-login to pick the landing page.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

const AuthContext = createContext(null);

const ANON_USER = {
  isAuthenticated: false,
  loading: true,
  user: null,           // { id, username, email, role, is_staff, is_superuser, owned_stores }
};

export function AuthProvider({ children }) {
  const [state, setState] = useState(ANON_USER);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setState({ ...ANON_USER, loading: false });
      return;
    }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/profile/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profile = res.data.profile || {};
      setState({
        isAuthenticated: true,
        loading: false,
        user: {
          id: profile.id,
          username: profile.username,
          email: profile.email,
          role: profile.role || 'customer',
          is_staff: !!profile.is_staff,
          is_superuser: !!profile.is_superuser,
          owned_stores: profile.owned_stores || [],
          display_name: profile.display_name || profile.username,
        },
      });
    } catch (err) {
      // 401 → wipe token and present as logged-out so RequireRole redirects.
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
      setState({ ...ANON_USER, loading: false });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    setState({ ...ANON_USER, loading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
